import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { IActionHandler, WebsocketCryptoMessage } from "@preveil-api";
import { useAppDispatch, useAppSelector, AccountWSSteps, SettingsErrorMessages, MessageHandlerDisplayType, Message, GlobalErrorMessages, MessageAnchors, ConfigKeys } from "src/common";
import { PageHeader } from "src/components";
import { DefaultPanel, AwaitConnection, ConnectionEstablished, SuccessPanel, ErrorPanel } from ".";
import { uiActions, WebsocketStatus, websocketActions, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";

function AddDeviceComponent() {
  const dispatch = useAppDispatch();
  const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCryptoMessage);
  const status = useAppSelector((state: RootState) => state.websocket.status);
  const errors = useAppSelector((state: RootState) => state.websocket.errors);
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const [step, setStep] = useState<number>(AccountWSSteps.DISCONNECTED);
  const crypto_websocket_server = getConfigValue(ConfigKeys.crypto_websocket_server);
  const user_id = current_account?.user_id;

  useEffect(() => {
    return () => {  // Destroy websocket State on leave
      dispatch(websocketActions.destroyWebsocket());
    };
  }, []);

  // Description: Responds to changes in message and websocket status
  useEffect(() => {
    const is_error = status === WebsocketStatus.error || message?.status === WebsocketStatus.error;
    let next = is_error ? AccountWSSteps.ERROR : !!message ? step + 1 : step;
    if (!!message?.status) {
      next = message.status === WebsocketStatus.key_transfer_complete ?
        AccountWSSteps.GET_KEY_TRANSFER_COMPLETE :
        message.status === WebsocketStatus.connection_established ?
          AccountWSSteps.GET_CONNECTION_ESTABLISHED : next;
    }
    setStep(next);
  }, [message, status]);

  // Description: load accounts and current account from crypto (First onload event)
  useEffect(() => {
    !!errors &&
      DeviceRequests.handlePageErrorMessage({ message: SettingsErrorMessages.default, stack: errors });
  }, [errors]);

  const DeviceRequests = {
    // Description: Initialize the connection
    handleInitWebsocketConnection: () => {
      if (!!user_id) {
        const host = `${crypto_websocket_server}/get/account/${encodeURIComponent(user_id)}/key/send`;
        dispatch(websocketActions.wsConnect({ host, init: { action: "init" } }));
      } else {
        DeviceRequests.handlePageError(SettingsErrorMessages.missing_user_id);
      }
    },
    // Description: Send Pin to server to request adding a device
    handleTransferKeyRequest: (params: { pin: string }) => {
      if (!!params.pin && params.pin.length > 0) {
        const verify_message: string = JSON.stringify({
          user_id,
          action: "verify",
          pin: params.pin
        });
        dispatch(websocketActions.wsOnSendMessage(verify_message));
      } else {
        DeviceRequests.handlePageError(SettingsErrorMessages.bad_pin);
      }
    },
    // Description: Reset message state and ws connection
    handleResetMessage: () => {
      dispatch(websocketActions.wsOnResetMessage());
    },
    // Description: Reset   ws connection
    handleDisconnect: () => {
      dispatch(websocketActions.wsDisconnect());
    },
    // Description: Reset page state and ws connection
    handleDestroy: () => {
      dispatch(websocketActions.destroyWebsocket());
      setStep(AccountWSSteps.DISCONNECTED);
    },
    handlePageError: (message: string) => {
      // dispatch(websocketActions.wsOnError(new Error(message)));
      dispatch(uiActions.handleRequestErrors(new Message(message)));
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DeviceRequests as any)[callback] instanceof Function) {
      (DeviceRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DeviceRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  // Action Handler
  const AddDevicePanels = () => {
    switch (step) {
      case AccountWSSteps.ERROR:
        return <ErrorPanel error={SettingsErrorMessages.default} handleAction={handlePageActions} />;
      case AccountWSSteps.DISCONNECTED:
        return <DefaultPanel handleAction={handlePageActions} />;
      case AccountWSSteps.INITIALIZE_CONNECTION:
      case AccountWSSteps.GET_ACCEPTED_INIT_OK:
        return <AwaitConnection handleAction={handlePageActions} />;
      case AccountWSSteps.GET_CONNECTION_ESTABLISHED:
        return <ConnectionEstablished handleAction={handlePageActions} />;
      case AccountWSSteps.GET_KEY_TRANSFER_COMPLETE:
        return <SuccessPanel handleAction={handlePageActions} />;
      default:
        return <DefaultPanel handleAction={handlePageActions} />;
    };
  };

  return <>
    <PageHeader>
      <h1>Add Device</h1>
    </PageHeader>
    <Card className="pv-card">
      {AddDevicePanels()}
    </Card>
  </>;
}

export default React.memo(AddDeviceComponent);
